import { create } from 'zustand';

/**
 * UI-level transient state — dialog open/close flags, loading overlay,
 * progress bar, and any ephemeral UI booleans.
 *
 * This store deliberately avoids business data (samples, inputs, config).
 * Those live in dedicated stores (Phase 2).
 */

interface LoadingState {
  active: boolean;
  progress: number; // 0–100
  message: string;
}

/** Payload for dialogs that carry dynamic content. */
export interface DialogPayloads {
  lengthLimit: { fieldLabel: string; minValue: number; maxValue: number };
  constraint: { message: string };
}

/** Persisted state for Explore Designs dialog (survives open/close). */
export interface ExploreState {
  metric: string | null;
  minVal: string;
  maxVal: string;
  lastBushingType: string | null;
}

interface UIState {
  // ── Loading overlay ──────────────────────────────────────
  loading: LoadingState;
  showLoading: (message?: string) => void;
  hideLoading: () => void;
  updateProgress: (progress: number, message?: string) => void;

  // ── Dialog visibility flags ──────────────────────────────
  configDialogOpen: boolean;
  moreDetailsDialogOpen: boolean;
  expectedTestDialogOpen: boolean;
  exploreDialogOpen: boolean;
  exploreInfoDialogOpen: boolean;
  infoDialogOpen: boolean;
  oodDialogOpen: boolean;
  lengthLimitDialogOpen: boolean;
  constraintDialogOpen: boolean;
  missingSORDialogOpen: boolean;
  syneraInfoDialogOpen: boolean;
  marcInfoDialogOpen: boolean;
  c1SnubberDialogOpen: boolean;
  betaTestingDialogOpen: boolean;
  computeConfirmDialogOpen: boolean;
  computeGuardDialogOpen: boolean;
  bushingTypeChangeDialogOpen: boolean;

  // ── Dialog payloads (dynamic content) ────────────────────
  lengthLimitPayload: DialogPayloads['lengthLimit'];
  constraintPayload: DialogPayloads['constraint'];
  computeGuardPayload: string[];

  // ── Explore Designs persisted state ──────────────────────
  exploreState: ExploreState;
  setExploreState: (patch: Partial<ExploreState>) => void;
  resetExploreState: () => void;

  openDialog: (name: DialogName) => void;
  closeDialog: (name: DialogName) => void;
  toggleDialog: (name: DialogName) => void;

  /** Open a dialog and set its payload in one action. */
  openLengthLimitDialog: (fieldLabel: string, minValue: number, maxValue: number) => void;
  openConstraintDialog: (message: string) => void;
  openComputeGuardDialog: (messages: string[]) => void;
  resetToken: number;

  /** Reset all UI state (loading, dialogs, payloads, explore state) back
   *  to first-load defaults. Does not touch business data — see the
   *  other stores (input/config/sample/job/exp) for those resets. */
  resetUI: () => void;
}

/** Union of every dialog key (without the "Open" suffix). */
export type DialogName =
  | 'configDialog'
  | 'moreDetailsDialog'
  | 'expectedTestDialog'
  | 'exploreDialog'
  | 'exploreInfoDialog'
  | 'infoDialog'
  | 'oodDialog'
  | 'lengthLimitDialog'
  | 'constraintDialog'
  | 'missingSORDialog'
  | 'syneraInfoDialog'
  | 'marcInfoDialog'
  | 'c1SnubberDialog'
  | 'betaTestingDialog'
  | 'computeConfirmDialog'
  | 'computeGuardDialog'
  | 'bushingTypeChangeDialog';

/** Derive the boolean key from the dialog name (e.g. "configDialog" → "configDialogOpen"). */
const openKey = (name: DialogName) => `${name}Open` as const;

const INITIAL_LOADING: LoadingState = { active: false, progress: 0, message: '' };

const INITIAL_LENGTH_LIMIT_PAYLOAD: DialogPayloads['lengthLimit'] = {
  fieldLabel: '',
  minValue: 0,
  maxValue: 0,
};

const INITIAL_CONSTRAINT_PAYLOAD: DialogPayloads['constraint'] = { message: '' };

const INITIAL_EXPLORE_STATE: ExploreState = {
  metric: null,
  minVal: '',
  maxVal: '',
  lastBushingType: null,
};

export const useUIStore = create<UIState>()((set) => ({
  // ── Loading ────────────────────────────────────────────────
  loading: { ...INITIAL_LOADING },

  showLoading: (message = 'Loading…') =>
    set({ loading: { active: true, progress: 0, message } }),

  hideLoading: () =>
    set({ loading: { active: false, progress: 0, message: '' } }),

  updateProgress: (progress, message) =>
    set((s) => ({
      loading: {
        ...s.loading,
        progress: Math.min(100, Math.max(0, progress)),
        ...(message !== undefined ? { message } : {}),
      },
    })),

  // ── Dialogs (all default closed) ─────────────────────────
  configDialogOpen: false,
  moreDetailsDialogOpen: false,
  expectedTestDialogOpen: false,
  exploreDialogOpen: false,
  exploreInfoDialogOpen: false,
  infoDialogOpen: false,
  oodDialogOpen: false,
  lengthLimitDialogOpen: false,
  constraintDialogOpen: false,
  missingSORDialogOpen: false,
  syneraInfoDialogOpen: false,
  marcInfoDialogOpen: false,
  c1SnubberDialogOpen: false,
  betaTestingDialogOpen: false,
  computeConfirmDialogOpen: false,
  computeGuardDialogOpen: false,
  bushingTypeChangeDialogOpen: false,

  // ── Dialog payloads ──────────────────────────────────────
  lengthLimitPayload: { ...INITIAL_LENGTH_LIMIT_PAYLOAD },
  constraintPayload: { ...INITIAL_CONSTRAINT_PAYLOAD },
  computeGuardPayload: [],

  // ── Explore Designs persisted state ──────────────────────
  exploreState: { ...INITIAL_EXPLORE_STATE },
  setExploreState: (patch) =>
    set((s) => ({ exploreState: { ...s.exploreState, ...patch } })),
  resetExploreState: () =>
    set({ exploreState: { ...INITIAL_EXPLORE_STATE } }),

  openDialog: (name) => set({ [openKey(name)]: true }),
  closeDialog: (name) => set({ [openKey(name)]: false }),
  toggleDialog: (name) =>
    set((s) => ({ [openKey(name)]: !(s[openKey(name)] as boolean) })),

  openLengthLimitDialog: (fieldLabel, minValue, maxValue) =>
    set({ lengthLimitDialogOpen: true, lengthLimitPayload: { fieldLabel, minValue, maxValue } }),

  openConstraintDialog: (message) =>
    set({ constraintDialogOpen: true, constraintPayload: { message } }),

  openComputeGuardDialog: (messages) =>
    set({ computeGuardDialogOpen: true, computeGuardPayload: messages }),
  resetToken: 0,

  resetUI: () =>
    set((s) => ({
      loading: { ...INITIAL_LOADING },
      configDialogOpen: false,
      moreDetailsDialogOpen: false,
      expectedTestDialogOpen: false,
      exploreDialogOpen: false,
      exploreInfoDialogOpen: false,
      infoDialogOpen: false,
      oodDialogOpen: false,
      lengthLimitDialogOpen: false,
      constraintDialogOpen: false,
      missingSORDialogOpen: false,
      syneraInfoDialogOpen: false,
      marcInfoDialogOpen: false,
      c1SnubberDialogOpen: false,
      betaTestingDialogOpen: false,
      computeConfirmDialogOpen: false,
      computeGuardDialogOpen: false,
      bushingTypeChangeDialogOpen: false,
      lengthLimitPayload: { ...INITIAL_LENGTH_LIMIT_PAYLOAD },
      constraintPayload: { ...INITIAL_CONSTRAINT_PAYLOAD },
      computeGuardPayload: [],
      exploreState: { ...INITIAL_EXPLORE_STATE },
      resetToken: s.resetToken + 1,
    })),
}));